import React from 'react';
import styled from '@benzinga/themetron';
import { Icon, Rate } from '@benzinga/core-ui';
import { SmartLink } from '@benzinga/analytics';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { Product } from '@benzinga/content-manager';
import { ProductFeatures } from '../Product/ProductFeatures';
import { BzImage } from '@benzinga/image';

interface ProductCardOptions {
  cta_button_text?: string;
  hide_attributes?: boolean;
  layout?: string;
  product?: boolean;
  product_link_type?: string;
  show_best_for?: boolean | string;
  show_commissions?: boolean | string;
  show_company_name?: boolean | string;
  show_cons?: boolean | string;
  show_pros?: boolean | string;
  show_star_rating?: boolean | string;
}

export interface CompareProductsCardsProps {
  options: ProductCardOptions;
  product: Product;
}

export const CompareProductsCards: React.FC<CompareProductsCardsProps> = ({ options, product }) => {
  return (
    <Container className="compare-product-table-container">
      <div className="top-section">
        <SmartLink href={product.link} label="Product Card Image">
          <BzImage alt={product.name} height={75} src={product.image} width={100} />
        </SmartLink>
        <Rate allowHalf className="rating-component" readOnly={true} value={product.rating} />
        {product.review ? <a href={product.review}>Read Review</a> : null}
      </div>

      {options?.show_best_for !== '0' && product.details?.best_for && (
        <ProductFeatures className="middle-section" features={product.details.best_for} type="best-for" />
      )}

      <div className="bottom-section">
        <SmartLink className="cta-link-button" href={product.link} label="Product Card CTA">
          Get Started <Icon icon={faLock} />
        </SmartLink>
      </div>
    </Container>
  );
};

const Container = styled.div`
  &.compare-product-table-container {
    background-color: ${({ theme }) => theme.colorPalette.white};
    border: solid 1px ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    max-width: 350px;
    min-width: 300px;

    &:hover {
      box-shadow: ${({ theme }) => theme.shadow.lg};
    }

    .top-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 15px 0;

      img {
        width: 200px;
      }
    }

    h4 {
      font-size: ${({ theme }) => theme.fontSize.sm};
      margin-bottom: 0 !important;
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
    }

    .middle-section {
      padding: 10px 14px;
      border-top: solid 1px ${({ theme }) => theme.colors.border};
    }

    .bottom-section {
      border-top: solid 1px ${({ theme }) => theme.colors.border};
      padding: 14px;
    }

    .best-for-span {
      font-size: ${({ theme }) => theme.fontSize.lg};
    }

    .cta-link-button {
      display: inline-block;
      height: 2.5rem;
      width: 100%;
      white-space: nowrap;
      border-radius: 0.25rem;
      background-color: rgba(0, 45, 69);
      padding: 0 0.875rem;
      text-align: center;
      font-family: sans-serif;
      font-size: 0.875rem;
      line-height: 1.25rem;
      font-weight: 700;
      text-transform: uppercase;
      line-height: 2.5rem;
      letter-spacing: 0.1em;
      color: #ffffff;
      text-decoration: none;
      cursor: pointer;
      box-shadow: ${({ theme }) => theme.shadow.sm};

      &:hover {
        background-color: rgb(217, 105, 0);
        box-shadow: ${({ theme }) => theme.shadow.md};
        position: relative;
        bottom: 2px;
      }

      > span {
        display: inline-block;
        margin-bottom: -2px;
      }
    }
  }
`;
